import {
  ArrowCounterClockwise,
  ArrowLeft,
  Bell,
  CalendarBlank,
  CalendarCheck,
  CaretRight,
  Check,
  Clock,
  CreditCard,
  DotsThree,
  Eye,
  ListChecks,
  Receipt,
  Scissors,
  SquaresFour,
  Trash,
  UserCircle,
  WarningCircle,
  X
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { DEPOSIT, dates, initialAppointments, services, staff, timeSlots } from "./data";
import { formatMoney, getBookingTotals, getDateLabel, getEligibleStaff, getSelectedServices, isBooked, isConflict } from "./logic";
import type { Appointment, BookingStep, Tab } from "./types";

const stepOrder: BookingStep[] = ["services", "staff", "slot", "review"];
const stepTitles: Record<BookingStep, string> = {
  home: "Đặt lịch",
  services: "Chọn dịch vụ",
  staff: "Chọn chuyên viên",
  slot: "Chọn ngày & giờ",
  review: "Kiểm tra lịch",
  success: "Đã giữ lịch"
};

const cloneInitialAppointments = () => initialAppointments.map((appointment) => ({ ...appointment }));

export function App() {
  const [tab, setTab] = useState<Tab>("book");
  const [step, setStep] = useState<BookingStep>("home");
  const [profilePrompt, setProfilePrompt] = useState(false);
  const [profileName, setProfileName] = useState("Khách");
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedDate, setSelectedDate] = useState(dates[0].iso);
  const [selectedTime, setSelectedTime] = useState("");
  const [note, setNote] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [serviceError, setServiceError] = useState("");
  const [slotError, setSlotError] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [appointments, setAppointments] = useState<Appointment[]>(cloneInitialAppointments);
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [lastBookingId, setLastBookingId] = useState<string | null>(null);
  const [toast, setToast] = useState("");

  const selectedServices = useMemo(() => getSelectedServices(selectedServiceIds), [selectedServiceIds]);
  const totals = useMemo(() => getBookingTotals(selectedServiceIds), [selectedServiceIds]);
  const eligibleStaff = useMemo(() => getEligibleStaff(selectedServiceIds), [selectedServiceIds]);
  const selectedStaff = staff.find((member) => member.id === selectedStaffId);
  const activeAppointments = appointments.filter((appointment) => appointment.status !== "cancelled");

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!cancelId) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCancelId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cancelId]);

  const beginBooking = () => {
    if (profileName === "Khách") {
      setProfilePrompt(true);
      return;
    }
    setStep("services");
  };

  const completeProfileChoice = (allow: boolean) => {
    setProfileName(allow ? "Mai" : "Khách");
    setProfilePrompt(false);
    setStep("services");
  };

  const resetDraft = () => {
    setSelectedServiceIds([]);
    setSelectedStaffId("");
    setSelectedDate(dates[0].iso);
    setSelectedTime("");
    setNote("");
    setPolicyAccepted(false);
    setServiceError("");
    setSlotError("");
    setReviewError("");
    setPaymentError("");
    setRescheduleId(null);
    setLastBookingId(null);
  };

  const resetDemo = () => {
    resetDraft();
    setProfileName("Khách");
    setAppointments(cloneInitialAppointments());
    setTab("book");
    setStep("home");
    setMenuOpen(false);
    setToast("Đã đặt lại dữ liệu demo.");
  };

  const showEmptyScenario = () => {
    setAppointments((current) => current.map((appointment) => ({ ...appointment, status: "cancelled" })));
    setTab("appointments");
    setMenuOpen(false);
    setToast("Đang xem tình huống lịch trống.");
  };

  const toggleService = (serviceId: string) => {
    setServiceError("");
    setSelectedStaffId("");
    setSelectedTime("");
    if (selectedServiceIds.includes(serviceId)) {
      setSelectedServiceIds((current) => current.filter((id) => id !== serviceId));
      return;
    }
    const next = [...selectedServiceIds, serviceId];
    if (next.length > 2 || getBookingTotals(next).duration > 120) {
      setServiceError("Lịch demo nhận tối đa 2 dịch vụ và 120 phút mỗi lượt.");
      return;
    }
    setSelectedServiceIds(next);
  };

  const goBack = () => {
    if (step === "services") {
      setStep("home");
      return;
    }
    if (step === "staff") setStep("services");
    if (step === "slot") {
      if (rescheduleId) {
        setRescheduleId(null);
        setTab("appointments");
      } else {
        setStep("staff");
      }
    }
    if (step === "review") setStep("slot");
    if (step === "success") {
      setTab("appointments");
      setStep("home");
    }
  };

  const continueFromServices = () => {
    if (!selectedServiceIds.length) {
      setServiceError("Chọn ít nhất một dịch vụ để tiếp tục.");
      return;
    }
    setStep("staff");
  };

  const continueFromStaff = () => {
    if (!selectedStaffId) return;
    setStep("slot");
  };

  const chooseSlot = (time: string) => {
    if (!selectedStaffId) return;
    setSlotError("");
    if (isConflict(selectedStaffId, selectedDate, time)) {
      setSelectedTime("");
      setSlotError("15:30 vừa được giữ trong lúc bạn chọn. 16:00 vẫn đủ thời gian.");
      return;
    }
    setSelectedTime(time);
  };

  const continueFromSlot = () => {
    if (!selectedTime) {
      setSlotError("Chọn một giờ còn trống để tiếp tục.");
      return;
    }
    if (rescheduleId) {
      setAppointments((current) =>
        current.map((appointment) =>
          appointment.id === rescheduleId
            ? { ...appointment, date: selectedDate, time: selectedTime, status: "rescheduled" }
            : appointment
        )
      );
      setRescheduleId(null);
      setSelectedTime("");
      setStep("home");
      setTab("appointments");
      setToast(`Đã chuyển lịch sang ${selectedTime}, ${getDateLabel(selectedDate)}.`);
      return;
    }
    setStep("review");
  };

  const confirmBooking = () => {
    setReviewError("");
    setPaymentError("");
    if (!policyAccepted) {
      setReviewError("Bạn cần xác nhận đã đọc chính sách đổi/huỷ.");
      return;
    }
    const id = `LUA-${selectedDate.slice(5).replace("-", "")}${selectedTime.replace(":", "")}`;
    const appointment: Appointment = {
      id,
      customer: profileName,
      serviceIds: [...selectedServiceIds],
      staffId: selectedStaffId,
      date: selectedDate,
      time: selectedTime,
      status: "upcoming",
      deposit: DEPOSIT,
      note
    };
    setAppointments((current) => [appointment, ...current]);
    setLastBookingId(id);
    setStep("success");
  };

  const tryPaymentFailure = () => {
    setPaymentError("Kịch bản thanh toán chưa hoàn tất. Không có tiền thật bị trừ.");
  };

  const startReschedule = (appointment: Appointment) => {
    setSelectedServiceIds([...appointment.serviceIds]);
    setSelectedStaffId(appointment.staffId);
    setSelectedDate(dates.find((date) => date.fullness !== "full")?.iso ?? dates[0].iso);
    setSelectedTime("");
    setRescheduleId(appointment.id);
    setTab("book");
    setStep("slot");
  };

  const confirmCancel = () => {
    if (!cancelId) return;
    setAppointments((current) =>
      current.map((appointment) =>
        appointment.id === cancelId ? { ...appointment, status: "cancelled" } : appointment
      )
    );
    setCancelId(null);
    setToast("Lịch đã huỷ. Cọc không được xử lý vì đây là mô phỏng.");
  };

  const switchTab = (nextTab: Tab) => {
    setTab(nextTab);
    if (nextTab === "book" && step === "success") setStep("home");
  };

  const rebookService = (serviceId: string) => {
    resetDraft();
    setSelectedServiceIds([serviceId]);
    setTab("book");
    setStep("staff");
  };

  const renderBook = () => {
    if (step === "home") {
      return (
        <section className="screen home-screen" aria-labelledby="home-title">
          <div className="home-image">
            <img src={`${import.meta.env.BASE_URL}media/swatch-table.webp`} alt="Bảng màu sơn móng và dụng cụ trên bàn" />
            <span className="image-label">BẢNG MÀU THÁNG 8</span>
          </div>
          <div className="home-copy">
            <p className="eyebrow">LỤA · HẢI CHÂU</p>
            <h1 id="home-title">Lịch đẹp, không cần nhắn qua lại.</h1>
            <p>Chọn dịch vụ, người làm và giờ còn trống. Giá và thời lượng luôn hiện trước khi xác nhận.</p>
          </div>
          <button className="primary-button large" type="button" onClick={beginBooking}>
            Chọn dịch vụ <CaretRight weight="bold" aria-hidden="true" />
          </button>
          <button className="upcoming-peek" type="button" onClick={() => setTab("appointments")}>
            <span className="peek-icon"><CalendarCheck weight="duotone" aria-hidden="true" /></span>
            <span>
              <strong>{activeAppointments.length} lịch sắp tới</strong>
              <small>Lịch gần nhất · T6 31.07 · 17:30</small>
            </span>
            <CaretRight aria-hidden="true" />
          </button>
          <div className="demo-note">
            <Eye aria-hidden="true" />
            <span>Profile, slot, ZNS và cọc đều là dữ liệu mô phỏng.</span>
          </div>
        </section>
      );
    }

    if (step === "services") {
      return (
        <section className="screen flow-screen" aria-labelledby="services-title">
          <FlowHeading title="Bạn muốn làm gì?" subtitle="Chọn tối đa 2 dịch vụ · tổng không quá 120 phút" id="services-title" />
          <div className="service-list">
            {services.map((service) => {
              const selected = selectedServiceIds.includes(service.id);
              return (
                <button
                  className={`service-option ${selected ? "selected" : ""}`}
                  type="button"
                  key={service.id}
                  onClick={() => toggleService(service.id)}
                  aria-pressed={selected}
                >
                  <span className="service-swatch" style={{ "--swatch": service.accent } as React.CSSProperties} />
                  <span className="service-copy">
                    <span className="service-meta">{service.category} · {service.duration} phút</span>
                    <strong>{service.name}</strong>
                    <small>{service.description}</small>
                    <b>{formatMoney(service.price)}</b>
                  </span>
                  <span className="check-box" aria-hidden="true">{selected ? <Check weight="bold" /> : null}</span>
                </button>
              );
            })}
          </div>
          {serviceError ? <InlineAlert tone="danger">{serviceError}</InlineAlert> : null}
          <StickyAction
            label={selectedServiceIds.length ? `Tiếp tục · ${formatMoney(totals.price)}` : "Chọn ít nhất một dịch vụ"}
            disabled={!selectedServiceIds.length}
            onClick={continueFromServices}
            summary={selectedServiceIds.length ? `${totals.duration} phút` : undefined}
          />
        </section>
      );
    }

    if (step === "staff") {
      return (
        <section className="screen flow-screen" aria-labelledby="staff-title">
          <FlowHeading title="Ai sẽ làm cho bạn?" subtitle={`${selectedServices.map((service) => service.name).join(" + ")} · ${totals.duration} phút`} id="staff-title" />
          <div className="staff-list" role="radiogroup" aria-label="Chuyên viên phù hợp">
            {eligibleStaff.map((member, index) => {
              const selected = selectedStaffId === member.id;
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`staff-option ${selected ? "selected" : ""}`}
                  key={member.id}
                  onClick={() => {
                    setSelectedStaffId(member.id);
                    setSelectedTime("");
                  }}
                >
                  <span className="staff-avatar" style={{ "--avatar": member.color } as React.CSSProperties}>{member.initials}</span>
                  <span>
                    <span className="service-meta">{index === 0 ? "Slot sớm nhất" : member.role}</span>
                    <strong>{member.name}</strong>
                    <small>{member.specialty}</small>
                  </span>
                  <span className="radio-dot" aria-hidden="true" />
                </button>
              );
            })}
          </div>
          <div className="quiet-card">
            <Clock aria-hidden="true" />
            <span>Chỉ hiển thị chuyên viên đủ kỹ năng cho toàn bộ dịch vụ đã chọn.</span>
          </div>
          <StickyAction
            label="Xem giờ còn trống"
            disabled={!selectedStaffId}
            onClick={continueFromStaff}
            summary={selectedStaff?.name}
          />
        </section>
      );
    }

    if (step === "slot") {
      const currentDate = dates.find((date) => date.iso === selectedDate)!;
      return (
        <section className="screen flow-screen slot-screen" aria-labelledby="slot-title">
          <FlowHeading
            title={rescheduleId ? "Chọn giờ mới" : "Ngày nào tiện cho bạn?"}
            subtitle={`${selectedStaff?.name ?? ""} · ${totals.duration} phút`}
            id="slot-title"
          />
          <div className="date-ribbon" role="radiogroup" aria-label="Ngày đặt lịch">
            {dates.map((date) => {
              const selected = selectedDate === date.iso;
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`date-chip ${selected ? "selected" : ""}`}
                  key={date.iso}
                  disabled={date.fullness === "full"}
                  onClick={() => {
                    setSelectedDate(date.iso);
                    setSelectedTime("");
                    setSlotError("");
                  }}
                >
                  <span>{date.weekday}</span>
                  <strong>{date.day}</strong>
                  <small>{date.fullness === "full" ? "Hết" : date.month}</small>
                </button>
              );
            })}
          </div>
          <div className="slot-context">
            <span>{getDateLabel(currentDate.iso)}</span>
            <span className={`capacity ${currentDate.fullness}`}>{currentDate.fullness === "tight" ? "Sắp kín" : "Còn chỗ"}</span>
          </div>
          <div className="slot-grid" role="radiogroup" aria-label="Giờ còn trống">
            {timeSlots.map((time) => {
              const booked = currentDate.fullness === "full" || isBooked(selectedStaffId, selectedDate, time);
              const selected = selectedTime === time;
              const conflict = isConflict(selectedStaffId, selectedDate, time);
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`slot-button ${selected ? "selected" : ""} ${conflict ? "conflict" : ""}`}
                  key={time}
                  disabled={booked}
                  onClick={() => chooseSlot(time)}
                >
                  <strong>{time}</strong>
                  <small>{booked ? "Hết" : conflict ? "Vừa giữ" : selected ? "Đã chọn" : "Còn"}</small>
                </button>
              );
            })}
          </div>
          {slotError ? (
            <InlineAlert tone="danger">
              <span>{slotError}</span>
              {slotError.startsWith("15:30") ? (
                <button type="button" className="inline-action" onClick={() => chooseSlot("16:00")}>Chọn 16:00</button>
              ) : null}
            </InlineAlert>
          ) : null}
          <div className="booking-summary-strip">
            <span>{selectedServices.map((service) => service.name).join(" + ")}</span>
            <strong>{formatMoney(totals.price)}</strong>
          </div>
          <StickyAction
            label={rescheduleId ? "Xác nhận đổi lịch" : "Tiếp tục"}
            disabled={!selectedTime}
            onClick={continueFromSlot}
            summary={selectedTime || "Chưa chọn giờ"}
          />
        </section>
      );
    }

    if (step === "review") {
      return (
        <section className="screen flow-screen review-screen" aria-labelledby="review-title">
          <FlowHeading title="Kiểm tra trước khi giữ lịch" subtitle="Không có thanh toán thật trong demo" id="review-title" />
          <div className="review-ticket">
            <div className="ticket-topline"><span>LỊCH TẠM GIỮ</span><span>{getDateLabel(selectedDate)}</span></div>
            <div className="ticket-time">{selectedTime}</div>
            <div className="ticket-rule" />
            <dl>
              <div><dt>Dịch vụ</dt><dd>{selectedServices.map((service) => service.name).join(" + ")}</dd></div>
              <div><dt>Chuyên viên</dt><dd>{selectedStaff?.name}</dd></div>
              <div><dt>Thời lượng</dt><dd>{totals.duration} phút</dd></div>
              <div><dt>Tổng</dt><dd>{formatMoney(totals.price)}</dd></div>
              <div><dt>Cọc mô phỏng</dt><dd>{formatMoney(DEPOSIT)}</dd></div>
            </dl>
          </div>
          <label className="field-label" htmlFor="booking-note">Điều LỤA nên biết</label>
          <textarea
            id="booking-note"
            value={note}
            maxLength={160}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Ví dụ: da nhạy cảm, cần tháo gel cũ…"
          />
          <label className="policy-check">
            <input
              type="checkbox"
              checked={policyAccepted}
              onChange={(event) => {
                setPolicyAccepted(event.target.checked);
                setReviewError("");
              }}
            />
            <span>
              <strong>Tôi đã đọc chính sách đổi/huỷ.</strong>
              <small>Đổi hoặc huỷ miễn phí trước 12 giờ. Cọc trong demo không được thu hoặc hoàn thật.</small>
            </span>
          </label>
          {reviewError ? <InlineAlert tone="danger">{reviewError}</InlineAlert> : null}
          {paymentError ? <InlineAlert tone="danger">{paymentError}</InlineAlert> : null}
          <button className="text-button" type="button" onClick={tryPaymentFailure}>Xem kịch bản lỗi cọc</button>
          <StickyAction
            label={`Mô phỏng cọc ${formatMoney(DEPOSIT)}`}
            onClick={confirmBooking}
            icon={<CreditCard weight="bold" aria-hidden="true" />}
          />
        </section>
      );
    }

    const booking = appointments.find((appointment) => appointment.id === lastBookingId);
    return (
      <section className="screen success-screen" aria-labelledby="success-title">
        <div className="success-mark" aria-hidden="true"><Check weight="bold" /></div>
        <p className="eyebrow">ĐÃ GIỮ LỊCH</p>
        <h1 id="success-title">Xong rồi, {profileName}.</h1>
        <p>Phiếu hẹn đã được thêm vào lịch sắp tới của bản demo.</p>
        {booking ? <AppointmentTicket appointment={booking} compact /> : null}
        <div className="zns-simulation">
          <Bell weight="duotone" aria-hidden="true" />
          <span>
            <strong>ZNS xác nhận · mô phỏng</strong>
            <small>Không có tin nhắn thật được gửi.</small>
          </span>
        </div>
        <button className="primary-button large" type="button" onClick={() => {
          resetDraft();
          setStep("home");
          setTab("appointments");
        }}>
          Xem lịch sắp tới <CaretRight weight="bold" aria-hidden="true" />
        </button>
        <button className="secondary-button" type="button" onClick={() => {
          resetDraft();
          setStep("services");
        }}>Đặt thêm lịch</button>
      </section>
    );
  };

  const renderAppointments = () => (
    <section className="screen appointment-screen" aria-labelledby="appointments-title">
      <FlowHeading title="Lịch sắp tới" subtitle="Dữ liệu cố định · 31.07.2026" id="appointments-title" />
      {activeAppointments.length ? (
        <div className="appointment-list">
          {activeAppointments.map((appointment) => {
            const appointmentServices = getSelectedServices(appointment.serviceIds);
            const member = staff.find((item) => item.id === appointment.staffId);
            return (
              <article className="appointment-card" key={appointment.id}>
                <div className="appointment-date">
                  <span>{getDateLabel(appointment.date)}</span>
                  <strong>{appointment.time}</strong>
                </div>
                <div className="appointment-body">
                  <span className={`status-tag ${appointment.status}`}>
                    {appointment.status === "rescheduled" ? "Đã đổi lịch" : appointment.locked ? "Dưới 12 giờ" : "Đã xác nhận"}
                  </span>
                  <h2>{appointmentServices.map((service) => service.name).join(" + ")}</h2>
                  <p>{member?.name} · {getBookingTotals(appointment.serviceIds).duration} phút</p>
                  {appointment.locked ? (
                    <InlineAlert tone="notice">Đã qua mốc tự đổi/huỷ. Liên hệ studio trong tình huống thật.</InlineAlert>
                  ) : (
                    <div className="appointment-actions">
                      <button type="button" onClick={() => startReschedule(appointment)}>
                        <CalendarBlank aria-hidden="true" /> Đổi lịch
                      </button>
                      <button type="button" className="danger-action" onClick={() => setCancelId(appointment.id)}>
                        <Trash aria-hidden="true" /> Huỷ
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <CalendarBlank weight="duotone" aria-hidden="true" />
          <h2>Chưa có lịch sắp tới.</h2>
          <p>Khi giữ lịch, phiếu hẹn sẽ nằm ở đây.</p>
          <button className="primary-button" type="button" onClick={() => {
            setTab("book");
            setStep("services");
          }}>Chọn dịch vụ</button>
        </div>
      )}
      <div className="policy-box">
        <strong>Chính sách demo</strong>
        <span>Đổi/huỷ trước 12 giờ · cọc hiển thị 100.000đ · không xử lý tiền thật.</span>
      </div>
    </section>
  );

  const renderCatalog = () => (
    <section className="screen catalog-screen" aria-labelledby="catalog-title">
      <FlowHeading title="Bảng dịch vụ" subtitle="Giá và thời lượng minh bạch trước khi đặt" id="catalog-title" />
      <img className="catalog-image" src={`${import.meta.env.BASE_URL}media/swatch-table.webp`} alt="Bảng màu sơn móng và dụng cụ trên bàn" />
      <div className="catalog-groups">
        {(["Nail", "Brow"] as const).map((category) => (
          <section key={category} aria-labelledby={`category-${category}`}>
            <div className="catalog-group-title">
              <span>{category === "Nail" ? <Scissors aria-hidden="true" /> : <Eye aria-hidden="true" />}</span>
              <h2 id={`category-${category}`}>{category}</h2>
            </div>
            {services.filter((service) => service.category === category).map((service) => (
              <div className="catalog-row" key={service.id}>
                <span className="catalog-line" style={{ "--swatch": service.accent } as React.CSSProperties} />
                <span>
                  <strong>{service.name}</strong>
                  <small>{service.duration} phút · {service.description}</small>
                </span>
                <span className="catalog-price">{formatMoney(service.price)}</span>
                <button type="button" onClick={() => rebookService(service.id)} aria-label={`Đặt ${service.name}`}>
                  Đặt
                </button>
              </div>
            ))}
          </section>
        ))}
      </div>
    </section>
  );

  const currentStepIndex = stepOrder.indexOf(step);
  const cancelAppointment = appointments.find((appointment) => appointment.id === cancelId);

  return (
    <main className="page-shell">
      <aside className="studio-panel" aria-label="Giới thiệu LỤA">
        <div className="studio-image-wrap">
          <img src={`${import.meta.env.BASE_URL}media/studio-hands.webp`} alt="Kỹ thuật viên sơn móng trên bàn dụng cụ sạch" />
          <div className="studio-wordmark">
            <span>LỤA</span>
            <small>NAIL & BROW · ĐÀ NẴNG</small>
          </div>
        </div>
        <div className="studio-copy">
          <p className="eyebrow">CHỌN ĐÚNG TAY NGHỀ · GIỮ ĐÚNG GIỜ</p>
          <h2>Một lịch hẹn rõ từ màu sơn đến phút bắt đầu.</h2>
          <div className="studio-facts">
            <span><b>5</b> dịch vụ</span>
            <span><b>3</b> chuyên viên</span>
            <span><b>0</b> dữ liệu gửi đi</span>
          </div>
          <p className="studio-disclaimer">LỤA là thương hiệu hư cấu. Bản web này mô phỏng trải nghiệm Zalo Mini App; không tích hợp Zalo SDK hoặc thanh toán.</p>
        </div>
      </aside>

      <section className="app-frame" aria-label="LỤA Zalo Mini App mô phỏng">
        <div className="simulation-rail">
          <span>ZALO MINI APP · MÔ PHỎNG</span>
          <span>09:41</span>
        </div>
        <header className="app-header">
          <button
            type="button"
            className={`icon-button ${step === "home" || tab !== "book" ? "invisible" : ""}`}
            onClick={goBack}
            aria-label="Quay lại"
            disabled={step === "home" || tab !== "book"}
          >
            <ArrowLeft weight="bold" aria-hidden="true" />
          </button>
          <div>
            <strong>LỤA</strong>
            <span>{tab === "book" ? stepTitles[step] : tab === "appointments" ? "Lịch hẹn" : "Dịch vụ"}</span>
          </div>
          <button type="button" className="host-menu-button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-label="Mở menu demo">
            <DotsThree weight="bold" aria-hidden="true" />
          </button>
          {menuOpen ? (
            <div className="host-menu" role="menu">
              <button type="button" role="menuitem" onClick={showEmptyScenario}><CalendarBlank /> Xem lịch trống</button>
              <button type="button" role="menuitem" onClick={resetDemo}><ArrowCounterClockwise /> Đặt lại dữ liệu</button>
            </div>
          ) : null}
        </header>
        {tab === "book" && currentStepIndex >= 0 ? (
          <div className="progress-track" aria-label={`Bước ${currentStepIndex + 1} trên 4`}>
            <span style={{ width: `${((currentStepIndex + 1) / 4) * 100}%` }} />
          </div>
        ) : null}

        <div className="app-content">
          {tab === "book" ? renderBook() : tab === "appointments" ? renderAppointments() : renderCatalog()}
        </div>

        <nav className="bottom-nav" aria-label="Điều hướng chính">
          <BottomNavButton active={tab === "book"} icon={<SquaresFour />} label="Đặt lịch" onClick={() => switchTab("book")} />
          <BottomNavButton active={tab === "appointments"} icon={<CalendarCheck />} label="Lịch hẹn" onClick={() => switchTab("appointments")} />
          <BottomNavButton active={tab === "services"} icon={<ListChecks />} label="Dịch vụ" onClick={() => switchTab("services")} />
        </nav>
      </section>

      {profilePrompt ? (
        <div className="sheet-backdrop" role="presentation">
          <section className="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="profile-title">
            <div className="sheet-grab" />
            <div className="profile-visual"><UserCircle weight="duotone" aria-hidden="true" /><span>MAI</span></div>
            <p className="eyebrow">HỒ SƠ ZALO · MÔ PHỎNG</p>
            <h2 id="profile-title">Điền nhanh tên “Mai”?</h2>
            <p>Demo chỉ dùng tên fixture trong bộ nhớ trình duyệt. Không truy cập hồ sơ Zalo thật.</p>
            <button className="primary-button large" type="button" onClick={() => completeProfileChoice(true)}>Cho phép mô phỏng</button>
            <button className="secondary-button" type="button" onClick={() => completeProfileChoice(false)}>Tiếp tục như khách</button>
          </section>
        </div>
      ) : null}

      {cancelAppointment ? (
        <div className="dialog-backdrop" role="presentation">
          <section className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
            <button className="dialog-close" type="button" onClick={() => setCancelId(null)} aria-label="Đóng"><X /></button>
            <WarningCircle className="dialog-icon" weight="duotone" aria-hidden="true" />
            <p className="eyebrow">HUỶ LỊCH DEMO</p>
            <h2 id="cancel-title">Huỷ lịch lúc {cancelAppointment.time}?</h2>
            <p>Hành động này chỉ đổi dữ liệu trong trình duyệt. Không có cọc thật được xử lý.</p>
            <button className="danger-button" type="button" onClick={confirmCancel}>Xác nhận huỷ</button>
            <button className="secondary-button" type="button" onClick={() => setCancelId(null)}>Giữ lịch này</button>
          </section>
        </div>
      ) : null}

      <div className={`toast ${toast ? "visible" : ""}`} role="status" aria-live="polite">
        <Check weight="bold" aria-hidden="true" /> {toast}
      </div>
    </main>
  );
}

function FlowHeading({ title, subtitle, id }: { title: string; subtitle: string; id: string }) {
  return (
    <div className="flow-heading">
      <p className="eyebrow">LỤA · ĐẶT LỊCH RÕ RÀNG</p>
      <h1 id={id}>{title}</h1>
      <p>{subtitle}</p>
    </div>
  );
}

function InlineAlert({ children, tone }: { children: React.ReactNode; tone: "danger" | "notice" }) {
  return (
    <div className={`inline-alert ${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <WarningCircle weight="fill" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

function StickyAction({
  label,
  disabled = false,
  onClick,
  summary,
  icon
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  summary?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="sticky-action">
      {summary ? <span>{summary}</span> : null}
      <button type="button" onClick={onClick} disabled={disabled}>
        {icon}{label}<CaretRight weight="bold" aria-hidden="true" />
      </button>
    </div>
  );
}

function BottomNavButton({ active, icon, label, onClick }: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button type="button" className={active ? "active" : ""} onClick={onClick} aria-current={active ? "page" : undefined}>
      {icon}<span>{label}</span>
    </button>
  );
}

function AppointmentTicket({ appointment, compact = false }: { appointment: Appointment; compact?: boolean }) {
  const appointmentServices = getSelectedServices(appointment.serviceIds);
  const member = staff.find((item) => item.id === appointment.staffId);
  return (
    <article className={`booking-ticket ${compact ? "compact" : ""}`}>
      <div className="ticket-punches" aria-hidden="true" />
      <div className="ticket-brand"><Receipt weight="duotone" aria-hidden="true" /><span>{appointment.id}</span></div>
      <div className="booking-ticket-time">
        <strong>{appointment.time}</strong>
        <span>{getDateLabel(appointment.date)}</span>
      </div>
      <p>{appointmentServices.map((service) => service.name).join(" + ")}</p>
      <small>{member?.name} · {getBookingTotals(appointment.serviceIds).duration} phút · Cọc mô phỏng {formatMoney(appointment.deposit)}</small>
    </article>
  );
}
